import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import useStyles from './style/FilterContent.style';
import { ExpandMore, EventOutlined } from '@mui/icons-material';
import { Accordion, AccordionSummary, AccordionDetails, Box, Typography } from '@mui/material';
import GridContainer from '../GridContainer';
import GridItem from '../GridItem';
import { FILTER_CONTENT_TYPE, FILTER_VALUES } from '../../../constants/FilterConstants';
import DateRangePicker from '../DateRangePicker';
import Datepicker from '../Datepicker';
import FilterAutoComplete from './FilterAutoComplete';
import Switch from '../Switch';

const FilterContent = ({ onChipChange, filterParams = {}, data = [] }) => {
  const classes = useStyles();
  const [customData, setCustomData] = useState(data);

  useEffect(() => {
    setCustomData(data);
  }, [data]);

  const renderFilterContent = (item, index) => {
    const { type } = item;
    switch (type) {
      case FILTER_CONTENT_TYPE.SELECT:
        return (
          <FilterAutoComplete
            item={item}
            customData={customData}
            setCustomData={setCustomData}
            onChipChange={onChipChange}
            filterParams={filterParams}
            index={index}
          />
        );

      case FILTER_CONTENT_TYPE.DATE_RANGE_PICKER: {
        const selectedDays = filterParams[item.value];
        return (
          <DateRangePicker
            selectedDays={selectedDays}
            onChange={(days) => onChipChange(item.value, days)}
            disabledAfterDate={new Date()}
          />
        );
      }

      case FILTER_CONTENT_TYPE.DATE_PICKER: {
        const rawValue = filterParams[item.value];
        const pickerValue = rawValue ? dayjs(rawValue) : null;
        const minDate = item?.minDate ? dayjs(item.minDate) : undefined;
        const maxDate = item?.maxDate ? dayjs(item.maxDate) : undefined;
        const start = filterParams[FILTER_VALUES.START_DATE];
        const end = filterParams[FILTER_VALUES.END_DATE];

        return (
          <Datepicker
            value={pickerValue && pickerValue.isValid() ? pickerValue : null}
            onChange={(nextDate) => {
              if (!nextDate || !dayjs(nextDate).isValid()) {
                onChipChange(item.value, '');
                return;
              }
              const dateStr = dayjs(nextDate).format('YYYY-MM-DD');
              if (item.value === FILTER_VALUES.END_DATE && start && dayjs(dateStr).isBefore(dayjs(start))) return;
              if (item.value === FILTER_VALUES.START_DATE && end && dayjs(dateStr).isAfter(dayjs(end))) {
                onChipChange(FILTER_VALUES.END_DATE, '');
              }
              onChipChange(item.value, dateStr);
            }}
            minDate={minDate}
            maxDate={maxDate}
            size="small"
          />
        );
      }

      case FILTER_CONTENT_TYPE.TOGGLE: {
        const selected = filterParams[item.value];
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginRight: '5px',
              opacity: item?.disabled ? 0.5 : 1,
            }}
            key={item.name}
          >
            <Typography>{item.name}</Typography>
            <Switch
              checked={selected}
              onChange={(e) => onChipChange(item.value, e.target.checked)}
              disabled={item.disabled}
            />
          </Box>
        );
      }

      default:
        return;
    }
  };

  return (
    <GridContainer direction={'column'} className={classes.filter} p={0}>
      {customData.length > 0 &&
        customData?.map((item, index) => {
          let defaultExpand = false;
          if (item.type === FILTER_CONTENT_TYPE.DATE_PICKER && filterParams[item.value]) {
            defaultExpand = true;
          } else if (item.value !== FILTER_VALUES.DATE_RANGE && filterParams[item.value]?.length > 0) {
            defaultExpand = true;
          } else if (
            item.value === FILTER_VALUES.DATE_RANGE &&
            filterParams[item.value]?.from &&
            filterParams[item.value]?.to &&
            filterParams[item.value]?.from !== '' &&
            filterParams[item.value]?.to !== ''
          ) {
            defaultExpand = true;
          }

          return (
            <GridItem key={index} xs={12}>
              {item.type === FILTER_CONTENT_TYPE.TOGGLE ? (
                renderFilterContent(item, index)
              ) : (
                <Accordion className={classes.accordion} defaultExpanded={defaultExpand}>
                  <AccordionSummary
                    expandIcon={
                      item.type === FILTER_CONTENT_TYPE.DATE_RANGE_PICKER ? <EventOutlined /> : <ExpandMore />
                    }
                    sx={
                      item.type === FILTER_CONTENT_TYPE.DATE_RANGE_PICKER
                        ? {
                            '& .MuiAccordionSummary-expandIconWrapper': {
                              transition: 'none',
                              '&.Mui-expanded': {
                                transform: 'none',
                              },
                            },
                          }
                        : {}
                    }
                  >
                    {item?.name}
                  </AccordionSummary>
                  <AccordionDetails>{renderFilterContent(item, index)}</AccordionDetails>
                </Accordion>
              )}
            </GridItem>
          );
        })}
    </GridContainer>
  );
};

export default FilterContent;
